import cx from 'classnames';
import includeSVG from 'assets/icons/check.svg';
import excludeSvg from 'assets/icons/exclude.svg';
import removeSvg from 'assets/icons/close.svg';
import { CodeNameModel } from 'components/common/types';
import { IncludeExcludeToggleProps } from './IncludeExcludeToggle.types';

const iconClass =
  'inline-block w-4 h-4 mx-0.5 bg-current [mask-size:contain] [mask-repeat:no-repeat] [mask-position:center]';

const getUpdatedOption = (
  option: CodeNameModel,
  isOptionNameSelectable: boolean,
  include: boolean,
  isDeselectAllowed: boolean,
): CodeNameModel => {
  if (option.include === include && !isOptionNameSelectable) {
    return isDeselectAllowed ? { ...option, include: undefined } : option;
  }

  return { ...option, include };
};

const IncludeExcludeToggle: React.FC<IncludeExcludeToggleProps> = ({
  onIncludeExcludeChange,
  option,
  isHovered,
  isSelected,
  isAlwaysVisible,
  isOptionNameSelectable,
  hideExclude = false,
  isDeselectAllowed = true,
  isDisabled = false,
}: IncludeExcludeToggleProps) => {
  if (!isAlwaysVisible && !isHovered && !isSelected) return null;

  return (
    <div className="flex bg-neutral-950-opacity-5 rounded-full">
      {!hideExclude && (
        <button
          aria-label="Exclude toggle"
          data-test-id="include-exclude-button-exclude"
          disabled={(!isSelected && !isAlwaysVisible) || isDisabled}
          className={cx('group flex items-center w-content h-[28px] p-3 rounded-full', {
            'bg-neutral-800 text-neutral-100': isSelected && !option.include,
            'cursor-not-allowed text-neutral-400': isDisabled,
            'cursor-pointer': !isDisabled,
          })}
          type="button"
          onClick={() => {
            const updatedOption = getUpdatedOption(option, isOptionNameSelectable, false, isDeselectAllowed);
            if (updatedOption === option) return;
            onIncludeExcludeChange(updatedOption);
          }}
        >
          <span
            className={cx(iconClass, '[mask-image:var(--icon)]', {
              'group-hover:[mask-image:var(--hover-icon)]': option.include === false && isDeselectAllowed,
            })}
            style={{ '--icon': `url(${excludeSvg})`, '--hover-icon': `url(${removeSvg})` } as React.CSSProperties}
          />
        </button>
      )}

      <button
        aria-label="Include toggle"
        data-test-id="include-exclude-button-include"
        disabled={(!isSelected && !isAlwaysVisible) || isDisabled}
        className={cx('group flex items-center w-content h-[28px] px-3 py-1.5 rounded-full', {
          'cursor-not-allowed text-neutral-400': isDisabled,
          'cursor-pointer': !isDisabled,
          'bg-primary-600 text-neutral-50': isSelected && option.include,
        })}
        type="button"
        onClick={() => {
          const updatedOption = getUpdatedOption(option, isOptionNameSelectable, true, isDeselectAllowed);
          if (updatedOption === option) return;
          onIncludeExcludeChange(updatedOption);
        }}
      >
        <span
          className={cx(iconClass, '[mask-image:var(--icon)]', {
            'group-hover:[mask-image:var(--hover-icon)]': option.include === true && isDeselectAllowed,
          })}
          style={{ '--icon': `url(${includeSVG})`, '--hover-icon': `url(${removeSvg})` } as React.CSSProperties}
        />
      </button>
    </div>
  );
};

export default IncludeExcludeToggle;
